/* istanbul ignore file */
import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import * as blueprint from '@adc/blueprint'
import configment from '@adc/configment'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Helmet from 'react-helmet'
import * as graph from '@adc/sdk'
import { standardSchemas } from '@adc/analytics'
import { safeStringify } from '@adc/core-utils/src/helpers/string'
import {
  TutorialTooltipActions,
  AuctionApplication,
  NullComponent,
  withApplicationNotificationsContext,
  hooks as adcHooks,
} from '@adc/ui-adc'
import { getDefaultNearbyRadius } from '../util/seekConversion'
import {
  getTitle,
  getDescription,
  getAddressKeyTerms,
} from '../converters/seoBuilder'
import auctionSmartAppImg from '../assets/auction_smart_banner_logo.svg'
import {
  getVirtualTitle,
  trackPageLoadWithFlags,
  trackVirtualSearchResults,
  trackSearchFilterChanged,
} from '../services/analytics'
import { fetchUserDetails } from '../actions/user'
import {
  closeLightbox,
  updateSavedSearch,
  savedSearchLookupForBlueprintSearch,
} from '../actions/savedSearch'
import { getMaxResultsAllowed } from '../util/maxResultsAllowed'
import { readCampaignParam } from '../util/campaign'
import { fetchAdContent as fetchAdContentAction } from '../actions/assets'
import SearchHeaderWithNotifications from '../components/searchHeaderWithNotifications'
import SaveSearchModal from '../components/saveSearchModal'
import { getFrequentlyAskedQuestions } from '../util/seoContent'
import { checkIsNearbySearch } from '../util/nearbyCheckbox'
import { NEARBY_SEARCH_RADIUS_OPTIONS } from '../constants/filters'
import { SaveSearchModal as NewMobileHeaderSaveButton } from './redesign/saveSearchModal'
import FancyNewFilters from './fancyNewFilters'
import CampaignWelcomeModal from './campaignWelcomeModal'
import MobileFilterBar from './mobileFilterBar'
import SearchPanel from './searchPanel'
import { filterDefinitionConfig } from './filterDefinitionConfig'

const BlueprintPoweredApp = ({
  user,
  featureFlags,
  incrementTutorial,
  savedSearch,
  onAuthenticationComplete,
  notifications,
  marketingTagsConfig,
  fetchAdContent,
  closeLightbox,
  updateSavedSearch,
  savedSearchLookupForBlueprintSearch,
  ssrHints,
}) => {
  const location = useLocation()
  const history = useHistory()

  // A curated SRP landing (mailer QR code) carries an opaque campaign token
  // `?c=XXXX`. Its presence suppresses the standard onboarding drawer and shows
  // a campaign welcome treatment instead. Absent/invalid -> default behavior.
  const campaign = useMemo(
    () => readCampaignParam(location.search),
    [location.search]
  )
  const [currentLocation] = useState(configment.get('REQUEST_LOCATION', null))

  const { data, actions } = blueprint.search.hooks.useListingsSearch({
    url: `/residential${location.pathname}${location.search}`,
    currentLocation,
    definitionConfig: filterDefinitionConfig,
    marketingTagsConfig,
    user,
    featureFlags,
    maxResultsAllowed: getMaxResultsAllowed(featureFlags),
    defaultNearbySearchRadius: getDefaultNearbyRadius(featureFlags),
    definitionGenerator:
      blueprint.search.buyerListingSearchFilters.newDefinitionGenerator,
    onFilterChanged: trackSearchFilterChanged,
  })

  useEffect(() => {
    if (data.currentSearchId) {
      const fullUrl = `${window.location.origin}${data.currentUrl}`

      // Fetch adglare content before each search
      fetchAdContent(fullUrl)
    }
  }, [data.currentSearchId, data.currentUrl, fetchAdContent])

  // Fire search-complete side effects when useListingsSearch finishes a search
  // (isLoading true -> false). These previously rode the redux assets sync;
  // results now render straight from the hook, so gate on the hook's own
  // loading transition instead of the assets fetchProgress latch.
  const wasLoadingRef = useRef(data.isLoading)
  // Property ids currently rendered on SRP. Held in a ref (not redux) so the
  // post-auth flow can tell the server which loaded properties to check saved
  // status for — the search hook doesn't refetch on login.
  const loadedPropertyIdsRef = useRef([])
  useEffect(() => {
    const finishedSearch = wasLoadingRef.current && !data.isLoading
    wasLoadingRef.current = data.isLoading

    if (finishedSearch && data.currentSearchId) {
      loadedPropertyIdsRef.current = (data.listings.content || [])
        .map(listing => listing.primary_property?.property_id)
        .filter(Boolean)
      savedSearchLookupForBlueprintSearch(data.listingCompatabilityFilters)

      trackVirtualSearchResults(
        data.currentUrl,
        data.listings.total_count || 0,
        getVirtualTitle({ ...data.currentFilters })
      )
    }
  }, [
    data.isLoading,
    data.currentSearchId,
    data.listingCompatabilityFilters,
    data.listings,
    data.currentFilters,
    data.currentUrl,
    savedSearchLookupForBlueprintSearch,
  ])

  useEffect(() => {
    if (data.currentUrl) {
      const newCurrentUrl = data.currentUrl.replace(/^\/residential\/?/, '/')
      const currentUrl =
        window.location.pathname.replace(/^\/residential\/?/, '/') +
        window.location.search

      if (newCurrentUrl !== currentUrl) {
        history.push(newCurrentUrl)
      }
    }
  }, [data.currentUrl, history])

  adcHooks.useMountEffect(() => {
    // TODO: adc-fe is now 6 feet under *draws cross on chest*. Investigate where this can possibly be set from now we are all on the same
    // stack.
    //
    // signup via adc-fe needs transient message
    const url = window.location.search

    if (url.indexOf('signupReferer') !== -1) {
      notifications.overlayBanner.open({ title: 'Thank you for signing up.' })

      // TODO: This is a specific error code that the pdp will sent in the case of an error. Perhaps we can make this
      // morpheus property service.
      // a constant and get away from coupling it to an API defined error code, which won't mean anything with the
      //
      // NOTE: I, corner, hereby declare that I knowingly and intentionally severed this. I discovered this code was
      // here, the pdp code was there, but it was not logically possible to ever enter into this row. As such, I removed
      // the pdp code, and am leaving here because very soon another ticket will appear that will seek to do a similiar
      // thing as this in pattern and I'd like to leave a reference to the existing implementation and notifier.
    } else if (url.indexOf('msgcode=PD-101') !== -1) {
      notifications.overlayBanner.open({
        title: 'This property is no longer available.',
        message: 'See similar properties nearby.',
      })
    }

    trackPageLoadWithFlags(user, featureFlags)
  })

  const singleFamilySchemas = (data.listings.content || [])
    .filter(
      listing =>
        listing?.primary_property?.summary.structure_type_group ===
        graph.enums.PropertyStructureTypeGroup.SingleFamily
    )
    .slice(0, 50)
    .map(a => {
      return standardSchemas.singleFamilyHomeSchema(a)
    })

  const ADC_LOGO = configment.get('AUCTIONCOM_LOGO_URL', '')
  const corporateSchema = useMemo(
    () => standardSchemas.corporateSchema(ADC_LOGO),
    [ADC_LOGO]
  )
  const searchSiteSchema = useMemo(() => standardSchemas.searchSiteSchema(), [])
  const organizationSchema = useMemo(
    () => standardSchemas.organizationSchema(ADC_LOGO),
    [ADC_LOGO]
  )

  const webpageSchema = useMemo(
    () =>
      standardSchemas.webPageSchema(
        'https://www.auction.com/residential',
        getTitle(
          data.currentFilters,
          featureFlags['fe.resi.seo.custom_title_tag']
        ),
        getDescription(
          data.currentFilters,
          featureFlags['fe.resi.seo.custom_meta_description_tag']
        ),
        [...getAddressKeyTerms(data.currentFilters)]
      ),
    [featureFlags, data.currentFilters]
  )

  // Minimal assets-shaped object for SEO helpers, sourced from the hook (the
  // redux assets slice no longer carries listings).
  const assetsForSeo = useMemo(
    () => ({
      list: data.listings.content || [],
      total: data.listings.total_count || 0,
      propertyTypeTotals: data.listings.propertyTypeTotals,
      remoteBideTotals: data.listings.remoteBideTotals,
    }),
    [data.listings]
  )

  const faqpageSchema = useMemo(
    () =>
      standardSchemas.faqPageSchema(
        'https://www.auction.com/residential',
        getTitle(
          data.currentFilters,
          featureFlags['fe.resi.seo.custom_title_tag']
        ),
        getDescription(
          data.currentFilters,
          featureFlags['fe.resi.seo.custom_meta_description_tag']
        ),
        [...getAddressKeyTerms(data.currentFilters)],
        getFrequentlyAskedQuestions(data.currentFilters, assetsForSeo)
      ),
    [featureFlags, data.currentFilters, assetsForSeo]
  )

  const helmetConfig = useMemo(
    () => ({
      title: getTitle(
        data.currentFilters,
        featureFlags['fe.resi.seo.custom_title_tag']
      ),
      description: getDescription(
        data.currentFilters,
        featureFlags['fe.resi.seo.custom_meta_description_tag']
      ),
      children: (
        <script type='application/ld+json'>
          {safeStringify({
            '@context': 'https://schema.org',
            '@graph': [
              corporateSchema,
              searchSiteSchema,
              organizationSchema,
              webpageSchema,
              faqpageSchema,
              ...singleFamilySchemas,
            ],
          })}
        </script>
      ),
    }),
    [
      corporateSchema,
      featureFlags,
      data.currentFilters,
      organizationSchema,
      searchSiteSchema,
      singleFamilySchemas,
      webpageSchema,
      faqpageSchema,
    ]
  )

  // Inject the currently-loaded property ids (held in a ref) so the post-auth
  // saved-state lookup is scoped to what's on screen, without a redux round-trip.
  const handleAuthenticationComplete = useCallback(
    authResult =>
      onAuthenticationComplete({
        ...authResult,
        propertyIds: loadedPropertyIdsRef.current,
      }),
    [onAuthenticationComplete]
  )

  const capabilities = useMemo(
    () => ({
      accessibility: {
        equalweb: {
          alignHorizontal: 'right',
          alignVertical: 'middle',
        },
      },
      authentication: {
        enabled: true,
        options: {
          onAuthenticationComplete: handleAuthenticationComplete,
        },
      },
      surveyMonkey: {
        enabled: false,
      },
      onboarding: {
        // Suppress the standard onboarding drawer for campaign visitors; the
        // campaign welcome drawer takes its place.
        enabled: !campaign,
        startTour: incrementTutorial,
      },
    }),
    [handleAuthenticationComplete, incrementTutorial, campaign]
  )

  const newMobileHeaderSaveButton = useMemo(
    () => <NewMobileHeaderSaveButton data={data} user={user} />,
    [data, user]
  )

  const searchHeaderWithNotifications = useMemo(() => {
    const customMobileContent = (
      <div
        style={{
          padding: '0 6px',
          background: '#fff',
          flex: '0 0 auto',
          minWidth: 0,
        }}
      >
        <FancyNewFilters
          actions={actions}
          data={data}
          featureFlags={featureFlags}
          modals={null} // Will be provided by the component via hooks
          onSaveSearch={() => {}} // Handled inside FancyNewFilters
          savedSearch={savedSearch}
          user={user}
          hideClearButton
        />
      </div>
    )

    return (
      <SearchHeaderWithNotifications
        user={user}
        featureFlags={featureFlags}
        filters={data.currentUiFilterGroups.locationFilterGroup}
        onFilterChange={actions.onFilterChange}
        onLocationSuggestionSelect={actions.onLocationSuggestionSelect}
        customMobileContent={customMobileContent}
        newMobileHeaderSaveButton={newMobileHeaderSaveButton}
        recognizedLocations={data.listings?.recognizedLocations}
      />
    )
  }, [
    actions,
    data,
    featureFlags,
    newMobileHeaderSaveButton,
    savedSearch,
    user,
  ])

  const [isMapView, setIsMapView] = useState(false)

  const mobileFilterBarElement = useMemo(() => {
    if (!featureFlags['fe.resi.enable-new-navigation']) {
      return null
    }

    const locationFilters =
      data.currentUiFilterGroups?.locationFilterGroup || {}
    const { city, zip, county, globalSearch } = locationFilters
    const showNearbyCheckbox =
      checkIsNearbySearch(city, zip, county) && globalSearch !== 'y'

    return (
      <MobileFilterBar
        data={data}
        actions={actions}
        updateFiltersAction={actions.onFilterChange}
        isMapView={isMapView}
        featureFlags={featureFlags}
        nearbyConfig={{
          showNearbyCheckbox,
          nearbyOptions: NEARBY_SEARCH_RADIUS_OPTIONS,
          defaultNearbyRadius: getDefaultNearbyRadius(featureFlags),
        }}
      />
    )
  }, [actions, data, featureFlags, isMapView])
  return (
    <AuctionApplication
      user={user}
      featureFlags={featureFlags}
      sspConfig={configment.get()}
      helmetConfig={helmetConfig}
      ssrHints={ssrHints}
      overridableComponents={{
        header: searchHeaderWithNotifications,
        footer: <NullComponent />,
      }}
      capabilities={capabilities}
    >
      <Helmet
        link={[
          { rel: 'android-touch-icon', href: auctionSmartAppImg },
          { rel: 'apple-touch-icon', href: auctionSmartAppImg },
        ]}
      />
      {mobileFilterBarElement}
      {campaign && <CampaignWelcomeModal campaign={campaign} />}
      <SearchPanel
        isLoading={data.isLoading}
        listings={data.listings}
        currentFilters={data.currentFilters}
        uiFilterGroups={data.currentUiFilterGroups}
        listingCompatabilityFilters={data.listingCompatabilityFilters}
        defaultUiFilterGroups={data.defaultUiFilterGroups}
        onFilterChange={actions.onFilterChange}
        onLayoutModeChange={setIsMapView}
        data={data}
        actions={actions}
      />
      {user.logged_in && (
        <>
          <SaveSearchModal
            savedSearch={savedSearch}
            currentFilters={data.currentFilters}
            listingCompatabilityFilters={data.listingCompatabilityFilters}
            closeLightbox={closeLightbox}
            updateSavedSearch={updateSavedSearch}
          />
        </>
      )}
    </AuctionApplication>
  )
}

BlueprintPoweredApp.propTypes = {
  user: PropTypes.object.isRequired,
  featureFlags: PropTypes.object.isRequired,
  incrementTutorial: PropTypes.func.isRequired,
  savedSearch: PropTypes.object.isRequired,
  onAuthenticationComplete: PropTypes.func.isRequired,
  notifications: PropTypes.object.isRequired,
  marketingTagsConfig: PropTypes.object.isRequired,
  fetchAdContent: PropTypes.func.isRequired,
  closeLightbox: PropTypes.func.isRequired,
  updateSavedSearch: PropTypes.func.isRequired,
  savedSearchLookupForBlueprintSearch: PropTypes.func.isRequired,
  ssrHints: PropTypes.object,
}

const mapStateToProps = store => ({
  marketingTagsConfig: store.marketingTagsConfig,
  featureFlags: store.featureFlags,
  user: store.user,
  savedSearch: store.savedSearch,
})

const mapActions = {
  incrementTutorial: TutorialTooltipActions.incrementTutorial,
  onAuthenticationComplete: ({ user, propertyIds }) =>
    fetchUserDetails(user, propertyIds),
  fetchAdContent: url => dispatch => fetchAdContentAction(dispatch, url),
  closeLightbox,
  updateSavedSearch,
  savedSearchLookupForBlueprintSearch,
}

export default withApplicationNotificationsContext(
  connect(mapStateToProps, mapActions)(BlueprintPoweredApp)
)
